import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { fetchExports } from "../../redux/reducers/user/slice";
import { allMonths } from "../../utils";
import Lottie from "lottie-react";
import Loading from "../../assets/lottie/animation.json";
import { getFileToDownload } from "../../api";

const MotionGrid = motion(Grid);
const MotionDiv = motion.div;

const options = {
  loop: true,
  autoplay: true,
  style: { width: 125, height: 125, margin: "auto" },
  animationData: Loading,
};

export default function HistoryTab() {
  const dispatch = useDispatch();
  const { exports, get_exports_loading } = useSelector((state) => state.user);
  const { email } = useSelector((state) => state.trello);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const itemsPerPage = 5;

  useEffect(() => {
    if (currentMonth) dispatch(fetchExports({ currentMonth, currentYear }));
  }, [currentMonth, currentYear, dispatch]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const getDayOfWeek = (dateString) => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const date = new Date(dateString);
    return days[date.getUTCDay()];
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];

    if (currentYear === 2024) {
      years.push(2024);
    } else {
      for (let year = 2024; year <= currentYear; year++) {
        years.push(year);
      }
    }

    return years;
  };

  const paginatedExports = exports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleDownloadFile = async (id, nameArquivo) => {
    try {
      setIsDownloading(true);

      const { arquivoBase64 } = await getFileToDownload(id);

      const link = document.createElement("a");
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${arquivoBase64}`;
      link.download = nameArquivo;
      link.click();
    } catch (error) {
      //console.error("Erro ao baixar o arquivo:", error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <MotionGrid
      initial={{ x: "100vw" }}
      animate={{ x: 0, transition: { type: "spring", mass: 0.4, damping: 8 } }}
      exit={{ opacity: 0, scale: 0.5, transition: { delay: 0.1 } }}
      container
      mt={5}
    >
      <Grid item xs={12}>
        <Grid
          item
          xs={12}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <FormControl fullWidth>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              label="Year"
              value={currentYear}
              onChange={(e) => setCurrentYear(e.target.value)}
            >
              {generateYears().map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ ml: 1 }}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              label="Month"
              value={currentMonth}
              onChange={(e) => setCurrentMonth(e.target.value)}
            >
              {allMonths.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {get_exports_loading && (
          <div className="w-100 d-flex flex-column justify-content-center align-items-center">
            <Lottie {...options} />
          </div>
        )}

        {!get_exports_loading && paginatedExports.length > 0 && (
          <Grid item xs={12} sx={{ mt: 1.5 }}>
            {paginatedExports.map((item, index) => (
              <MotionDiv
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                style={{
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Typography variant="body1">
                    <strong>Type:</strong> {item.textEmailDestinatario ? "Scheduled" : "Online Exported"}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Email:</strong> {item.textEmailDestinatario || email}
                  </Typography>
                  <Typography variant="body1">
                    <strong>File Name:</strong> {item.nameArquivo}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Date:</strong>{" "}
                    {new Date(item.dateRegistro).toLocaleString()} (
                    {getDayOfWeek(item.dateRegistro)})
                  </Typography>
                </div>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={isDownloading}
                  onClick={() => handleDownloadFile(item.id, item.nameArquivo)}
                >
                  {isDownloading ? "Downloading..." : "Download File"}
                </Button>
              </MotionDiv>
            ))}
            <Grid container justifyContent="center" sx={{ mt: 2 }}>
              <Pagination
                count={Math.ceil(exports.length / itemsPerPage)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Grid>
          </Grid>
        )}

        {!get_exports_loading && exports.length === 0 && (
          <Typography variant="body1" textAlign="center" sx={{ mt: 3 }}>
            No data available for this month.
          </Typography>
        )}
      </Grid>
    </MotionGrid>
  );
}
